import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Mail, UserCheck, UserX, Trash2, Clock, Filter, Loader2, Search,
  Download, ChevronDown, ChevronUp, MapPin, Globe, StickyNote, Users, X, Eye
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config/api';

const STATUS_STYLES = {
  waiting: 'bg-amber-50 text-amber-700 border-amber-200',
  invited: 'bg-blue-50 text-blue-700 border-blue-200',
  converted: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

const TYPE_STYLES = {
  tradesperson: 'bg-purple-50 text-purple-700 border-purple-200',
  employer: 'bg-orange-50 text-orange-700 border-orange-200',
};

function Badge({ text, style }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${style}`}>{text}</span>;
}

export default function AdminWaitlist() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [entries, setEntries] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ status: '', user_type: '', area_id: '', trade: '' });
  const [searchTerm, setSearchTerm] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [notesModal, setNotesModal] = useState(null);
  const [notesText, setNotesText] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      if (res.data.user_type !== 'admin') { navigate('/login'); return; }
      setUser(res.data);
    } catch { navigate('/login'); }
  }, [navigate]);

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.user_type) params.append('user_type', filters.user_type);
      if (filters.area_id) params.append('area_id', filters.area_id);
      if (filters.trade) params.append('trade', filters.trade);

      const [entriesRes, areasRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/waitlist?${params.toString()}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/admin/regions`, { withCredentials: true }),
      ]);
      setEntries(entriesRes.data || []);
      setAreas(areasRes.data || []);
    } catch {
      toast.error('Failed to fetch waitlist');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { checkAuth(); }, [checkAuth]);
  useEffect(() => { if (user) fetchData(); }, [user, fetchData, filters]);

  const handleInvite = async (entryId) => {
    setActionLoading(entryId);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/waitlist/${entryId}/invite`, {}, { withCredentials: true });
      toast.success('Invitation sent');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send invite');
    } finally { setActionLoading(null); }
  };

  const handleConvert = async (entryId) => {
    setActionLoading(entryId);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/waitlist/${entryId}/convert`, {}, { withCredentials: true });
      toast.success('Entry converted');
      fetchData();
    } catch { toast.error('Failed to convert'); }
    finally { setActionLoading(null); }
  };

  const handleReject = async (entryId) => {
    if (!window.confirm('Reject this waitlist entry?')) return;
    setActionLoading(entryId);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/waitlist/${entryId}/reject`, { notes: 'Rejected by admin' }, { withCredentials: true });
      toast.success('Entry rejected');
      fetchData();
    } catch { toast.error('Failed to reject'); }
    finally { setActionLoading(null); }
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Permanently delete this entry?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/waitlist/${entryId}`, { withCredentials: true });
      toast.success('Entry deleted');
      fetchData();
    } catch { toast.error('Failed to delete'); }
  };

  const handleSaveNotes = async () => {
    if (!notesModal) return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/waitlist/${notesModal}/notes`, { notes: notesText }, { withCredentials: true });
      toast.success('Notes saved');
      setNotesModal(null);
      fetchData();
    } catch { toast.error('Failed to save notes'); }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.area_id) params.append('area_id', filters.area_id);
      if (filters.user_type) params.append('user_type', filters.user_type);
      const res = await axios.get(`${BACKEND_URL}/api/admin/waitlist/export?${params.toString()}`, {
        withCredentials: true, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'waitlist_export.csv';
      a.click();
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const getAreaName = (areaId) => {
    const area = areas.find(a => a.region_id === areaId);
    return area ? area.name : null;
  };

  const filtered = entries.filter(e => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (
      (e.full_name || e.name || '').toLowerCase().includes(t) ||
      (e.email || '').toLowerCase().includes(t) ||
      (e.postcode || '').toLowerCase().includes(t) ||
      (e.main_trade || e.trade_type || '').toLowerCase().includes(t) ||
      (e.company_name || '').toLowerCase().includes(t)
    );
  });

  const stats = {
    total: entries.length,
    waiting: entries.filter(e => e.status === 'waiting').length,
    invited: entries.filter(e => e.status === 'invited').length,
    converted: entries.filter(e => e.status === 'converted').length,
    legacy: entries.filter(e => e.is_legacy).length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/admin/regions')} className="text-slate-600 hover:text-slate-900" data-testid="back-btn"><ArrowLeft className="h-5 w-5" /></button>
              <div className="flex items-center gap-2">
                <Users className="h-7 w-7 text-orange-500" />
                <div>
                  <h1 className="text-lg font-bold text-slate-900">Waitlist Management</h1>
                  <p className="text-xs text-slate-500">{stats.total} entries total</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={handleExport} className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors" data-testid="export-csv-btn">
                <Download className="h-4 w-4" /> Export CSV
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-4">
        {/* Stats Strip */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="waitlist-stats">
          <div className="bg-white rounded-lg border border-slate-200 p-3 text-center">
            <p className="text-xl font-bold text-slate-900">{stats.total}</p>
            <p className="text-xs text-slate-500">Total</p>
          </div>
          <div className="bg-amber-50 rounded-lg border border-amber-200 p-3 text-center">
            <p className="text-xl font-bold text-amber-700">{stats.waiting}</p>
            <p className="text-xs text-amber-600">Waiting</p>
          </div>
          <div className="bg-blue-50 rounded-lg border border-blue-200 p-3 text-center">
            <p className="text-xl font-bold text-blue-700">{stats.invited}</p>
            <p className="text-xs text-blue-600">Invited</p>
          </div>
          <div className="bg-emerald-50 rounded-lg border border-emerald-200 p-3 text-center">
            <p className="text-xl font-bold text-emerald-700">{stats.converted}</p>
            <p className="text-xs text-emerald-600">Converted</p>
          </div>
          <div className="bg-slate-50 rounded-lg border border-slate-200 p-3 text-center">
            <p className="text-xl font-bold text-slate-600">{stats.legacy}</p>
            <p className="text-xs text-slate-500">Legacy</p>
          </div>
        </div>

        {/* Search + Filters */}
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input data-testid="search-waitlist" type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Search by name, email, postcode, trade..." />
            </div>
            <button onClick={() => setShowFilters(!showFilters)} data-testid="toggle-filters-btn"
              className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
              <Filter className="h-4 w-4" /> Filters {showFilters ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
          </div>
          {showFilters && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3 pt-3 border-t border-slate-100">
              <select data-testid="filter-status" value={filters.status} onChange={e => setFilters(p => ({...p, status: e.target.value}))}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                <option value="">All Statuses</option>
                <option value="waiting">Waiting</option>
                <option value="invited">Invited</option>
                <option value="converted">Converted</option>
                <option value="rejected">Rejected</option>
              </select>
              <select data-testid="filter-user-type" value={filters.user_type} onChange={e => setFilters(p => ({...p, user_type: e.target.value}))}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                <option value="">All Types</option>
                <option value="tradesperson">Tradesperson</option>
                <option value="employer">Employer</option>
              </select>
              <select data-testid="filter-area" value={filters.area_id} onChange={e => setFilters(p => ({...p, area_id: e.target.value}))}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500">
                <option value="">All Areas</option>
                {areas.map(a => <option key={a.region_id} value={a.region_id}>{a.name}</option>)}
              </select>
              <input data-testid="filter-trade" type="text" value={filters.trade} onChange={e => setFilters(p => ({...p, trade: e.target.value}))}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" placeholder="Filter by trade..." />
            </div>
          )}
        </div>

        {/* Entries List */}
        {filtered.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
            <Users className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No waitlist entries found</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="waitlist-entries">
            {filtered.map(entry => {
              const name = entry.full_name || entry.name || 'Unknown';
              const isExpanded = expandedEntry === entry.entry_id;
              const areaName = getAreaName(entry.matched_area_id);
              const isLoading = actionLoading === entry.entry_id;

              return (
                <div key={entry.entry_id} data-testid={`entry-${entry.entry_id}`}
                  className="bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-sm transition-shadow">
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <button onClick={() => navigate(`/admin/waitlist/${entry.entry_id}`)} className="font-semibold text-slate-900 truncate hover:text-orange-600 hover:underline text-left" data-testid={`view-name-${entry.entry_id}`}>{name}</button>
                          <Badge text={entry.user_type === 'tradesperson' ? 'Trade' : 'Employer'} style={TYPE_STYLES[entry.user_type] || TYPE_STYLES.employer} />
                          <Badge text={entry.status.charAt(0).toUpperCase() + entry.status.slice(1)} style={STATUS_STYLES[entry.status] || STATUS_STYLES.waiting} />
                          {entry.is_legacy && <span className="text-xs text-slate-400 italic">Legacy</span>}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1.5 text-xs text-slate-500">
                          <span>{entry.email}</span>
                          {entry.postcode && <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{entry.postcode}</span>}
                          {entry.mapped_region && <span className="flex items-center gap-0.5"><Globe className="h-3 w-3" />{entry.mapped_region}</span>}
                          {!entry.mapped_region && areaName && <span className="flex items-center gap-0.5"><Globe className="h-3 w-3" />{areaName}</span>}
                          {(entry.main_trade || entry.trade_type) && <span>{entry.main_trade || entry.trade_type}</span>}
                          {entry.company_name && <span>{entry.company_name}</span>}
                          <span className="flex items-center gap-0.5"><Clock className="h-3 w-3" />{new Date(entry.created_at).toLocaleDateString('en-GB')}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        {entry.status === 'waiting' && (
                          <>
                            <button onClick={() => handleInvite(entry.entry_id)} disabled={isLoading} title="Invite"
                              className="p-1.5 text-blue-500 hover:bg-blue-50 rounded disabled:opacity-50" data-testid={`invite-${entry.entry_id}`}>
                              {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                            </button>
                            <button onClick={() => handleConvert(entry.entry_id)} disabled={isLoading} title="Convert"
                              className="p-1.5 text-emerald-500 hover:bg-emerald-50 rounded disabled:opacity-50" data-testid={`convert-${entry.entry_id}`}>
                              <UserCheck className="h-4 w-4" />
                            </button>
                            <button onClick={() => handleReject(entry.entry_id)} disabled={isLoading} title="Reject"
                              className="p-1.5 text-red-500 hover:bg-red-50 rounded disabled:opacity-50" data-testid={`reject-${entry.entry_id}`}>
                              <UserX className="h-4 w-4" />
                            </button>
                          </>
                        )}
                        <button onClick={() => navigate(`/admin/waitlist/${entry.entry_id}`)} title="View Details"
                          className="p-1.5 text-orange-500 hover:bg-orange-50 rounded" data-testid={`view-${entry.entry_id}`}>
                          <Eye className="h-4 w-4" />
                        </button>
                        <button onClick={() => { setNotesModal(entry.entry_id); setNotesText(entry.notes || ''); }} title="Notes"
                          className="p-1.5 text-slate-400 hover:bg-slate-50 rounded" data-testid={`notes-${entry.entry_id}`}>
                          <StickyNote className="h-4 w-4" />
                        </button>
                        <button onClick={() => handleDelete(entry.entry_id)} title="Delete"
                          className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded" data-testid={`delete-${entry.entry_id}`}>
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <button onClick={() => setExpandedEntry(isExpanded ? null : entry.entry_id)}
                          className="p-1.5 text-slate-400 hover:bg-slate-50 rounded" data-testid={`expand-${entry.entry_id}`}>
                          {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-slate-100 p-4 bg-slate-50/50 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      {entry.mobile_number && <div><span className="text-slate-500">Phone:</span> {entry.mobile_number}</div>}
                      {entry.postcode_district && <div><span className="text-slate-500">District:</span> {entry.postcode_district}</div>}
                      {entry.mapped_region && <div><span className="text-slate-500">Region:</span> {entry.mapped_region}</div>}
                      {entry.town_city && <div><span className="text-slate-500">Town:</span> {entry.town_city}</div>}
                      {entry.county && <div><span className="text-slate-500">County:</span> {entry.county}</div>}
                      {entry.willing_to_work_areas?.length > 0 && <div className="col-span-2"><span className="text-slate-500">Willing to work:</span> {entry.willing_to_work_areas.join(', ')}</div>}
                      {entry.ready_to_start && <div><span className="text-slate-500">Ready:</span> {entry.ready_to_start}</div>}
                      {entry.need_timing && <div><span className="text-slate-500">Need:</span> {entry.need_timing}</div>}
                      {entry.hiring_frequency && <div><span className="text-slate-500">Frequency:</span> {entry.hiring_frequency}</div>}
                      {entry.travel_radius_miles && <div><span className="text-slate-500">Radius:</span> {entry.travel_radius_miles} mi</div>}
                      {entry.area_status_at_signup && <div><span className="text-slate-500">Area at signup:</span> {entry.area_status_at_signup}</div>}
                      {entry.referral_code && <div><span className="text-slate-500">Referral:</span> {entry.referral_code}</div>}
                      {entry.source && <div><span className="text-slate-500">Source:</span> {entry.source}</div>}
                      {entry.additional_trades?.length > 0 && <div className="col-span-2"><span className="text-slate-500">Extra trades:</span> {entry.additional_trades.join(', ')}</div>}
                      {entry.required_trades?.length > 0 && <div className="col-span-2"><span className="text-slate-500">Needs:</span> {entry.required_trades.join(', ')}</div>}
                      {entry.notes && <div className="col-span-full"><span className="text-slate-500">Notes:</span> {entry.notes}</div>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Notes Modal */}
      {notesModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setNotesModal(null)}>
          <div className="bg-white rounded-xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="font-bold text-slate-900">Entry Notes</h3>
              <button onClick={() => setNotesModal(null)} data-testid="close-notes-btn"><X className="h-5 w-5 text-slate-400" /></button>
            </div>
            <div className="p-4">
              <textarea data-testid="notes-textarea" value={notesText} onChange={e => setNotesText(e.target.value)}
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500" rows={4} placeholder="Add notes..." />
            </div>
            <div className="flex justify-end gap-3 p-4 border-t border-slate-100">
              <button onClick={() => setNotesModal(null)} className="px-4 py-2 text-sm text-slate-600">Cancel</button>
              <button onClick={handleSaveNotes} data-testid="save-notes-btn" className="px-4 py-2 bg-orange-500 text-white text-sm rounded-lg hover:bg-orange-600">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
